import { REPORT_VISIBLE_COLUMNS } from '../hooks/tables-hooks';
import { baseColumns as rideBaseColumns } from '../ride-reports/helpers';
import {
  AvgRideCostPerMemberApiItem,
  AvgRideCostPerMemberApiResponse,
  MemberActiveInactiveApiItem,
  NoShowMonthItem,
} from '@component/LightVersion/Reports/member-reports/MemberReportsDashboard';
import { AvgRideCostPerMemberItem } from '@component/LightVersion/Reports/ReportsComponents/AvgRideCostPerMemberChart/AvgRideCostPerMemberChart';
import { MemberActiveInactiveItem } from '@component/LightVersion/Reports/ReportsComponents/MemberActiveInactiveChart/MemberActiveInactiveChart';
import { RIDE_REPORT_TYPES } from '@component/LightVersion/Reports/ride-reports/constants';

export type MemberReportConfig = {
  reportId: string;
  title: string;
  tab?: string;
  aggregated?: boolean;
  rowActions?: string[];
  columns?: Array<{
    field: string;
    label: string;
    order: number;
    defaultVisible: boolean;
  }>;
};

type MemberReportColumnOverride = {
  field: string;
  label: string;
  order: number;
  defaultVisible: boolean;
};

type MemberReportColumnDefinition = readonly [field: string, label: string];

export const MEMBER_REPORT_IDS = {
  NO_SHOW_MEMBERS: 'no-show-members',
  CONSECUTIVE_NO_SHOW: 'consecutive-no-show',
  HIGH_MILEAGE_MEMBERS: 'high-mileage-members',
  ACTIVE_MEMBERS: 'active-members',
  MOST_USED_TAGS: 'most-used-tags',
  MEMBERS_BY_TAGS: 'members-by-tags',
  MEMBERS_BY_TAG: 'members-by-tag',
  TAGS_USED: 'tags-used',
  MEMBERS_BY_RENDERING_SERVICE: 'members-by-rendering-service',
  RENDERING_SERVICE_MEMBERS: 'rendering-service-members',
  MEMBERS_BY_LOB: 'members-by-lob',
  MEMBERS_BY_COUNTY: 'members-by-county',
  MONTHLY_MEMBER_ACTIVITY: 'monthly-member-activity',
  MONTHLY_MEMBER_ACTIVE_INACTIVE: 'monthly-member-active-inactive',
  AVG_RIDE_AND_COST_PER_MEMBER: 'avg-ride-and-cost-per-member',
  MONTHLY_AVG_RIDE_AND_COST_PER_MEMBER: 'monthly-avg-ride-cost-per-member',
  LEGACY_MONTHLY_AVG_RIDE_AND_COST_PER_MEMBER:
    'monthly-avg-ride-and-cost-per-member',
  MONTHLY_MEMBERS_CREATED: 'monthly-members-created',
  UTILIZING_MEMBERS_BY_AGE_GROUP: 'utilizing-members-by-age-group',
  AGE_GROUP_ANALYSIS: 'age-group-analysis',
} as const;

type MemberReportId =
  (typeof MEMBER_REPORT_IDS)[keyof typeof MEMBER_REPORT_IDS];

export const memberHideDashboardRoutes = Object.values(MEMBER_REPORT_IDS);

const TAG_REPORT_IDS = [
  MEMBER_REPORT_IDS.MOST_USED_TAGS,
  MEMBER_REPORT_IDS.MEMBERS_BY_TAGS,
  MEMBER_REPORT_IDS.MEMBERS_BY_TAG,
  MEMBER_REPORT_IDS.TAGS_USED,
] as const;

const RENDERING_SERVICE_REPORT_IDS = [
  MEMBER_REPORT_IDS.MEMBERS_BY_RENDERING_SERVICE,
  MEMBER_REPORT_IDS.RENDERING_SERVICE_MEMBERS,
] as const;

const AVG_RIDE_AND_COST_REPORT_IDS = [
  MEMBER_REPORT_IDS.AVG_RIDE_AND_COST_PER_MEMBER,
  MEMBER_REPORT_IDS.MONTHLY_AVG_RIDE_AND_COST_PER_MEMBER,
  MEMBER_REPORT_IDS.LEGACY_MONTHLY_AVG_RIDE_AND_COST_PER_MEMBER,
] as const;

const COMMON_MEMBER_COLUMN_DEFINITIONS = [
  ['fullName', 'Member Name'],
  ['memberIdNumber', 'MemberId'],
  ['status', 'Member Status'],
  ['firstDateOfService', 'First Date of Service'],
  ['lastDateOfService', 'Last Date of Service'],
  ['lineOfBusiness', 'Member LOB'],
] as const satisfies readonly MemberReportColumnDefinition[];

const AGGREGATE_MEMBER_COLUMN_DEFINITIONS = [
  ['firstDateOfService', 'First Date of Service'],
  ['lastDateOfService', 'Last Date of Service'],
] as const satisfies readonly MemberReportColumnDefinition[];

const MONTHLY_MEMBER_COLUMN_DEFINITIONS = [
  ['fullName', 'Member Name'],
  ['memberIdNumber', 'MemberId'],
  ['status', 'Member Status'],
  ['dateOfServiceMonthYear', 'Date of Service Month + Year'],
  ['contactNumber', 'Contact Number'],
  ['lineOfBusiness', 'Member LOB'],
] as const satisfies readonly MemberReportColumnDefinition[];

const AVG_RIDE_AND_COST_COLUMN_DEFINITIONS = [
  ['fullName', 'Member Name'],
  ['memberIdNumber', 'MemberId'],
  ['status', 'Member Status'],
  ['dateOfServiceMonthYear', 'Date of Service Month + Year'],
  ['totalCost', 'Total Cost Per Member'],
  ['totalRides', 'Count Ride segments by Member'],
  ['avgCostPerMember', 'Average Cost Per Member'],
] as const satisfies readonly MemberReportColumnDefinition[];

const MEMBER_ACTIVE_INACTIVE_MONTHLY_COLUMN_DEFINITIONS = [
  ['fullName', 'Member Name'],
  ['memberIdNumber', 'MemberId'],
  ['status', 'Member Status'],
  ['dateOfServiceMonthYear', 'Date of Service Month + Year'],
  ['contactNumber', 'Contact Number'],
  ['lineOfBusiness', 'Member LOB'],
  ['totalRides', 'Count Ride segments by Member'],
] as const satisfies readonly MemberReportColumnDefinition[];

const NEW_MEMBERS_ADDED_COLUMN_DEFINITIONS = [
  ['fullName', 'Member Name'],
  ['memberIdNumber', 'MemberId'],
  ['status', 'Member Status'],
  ['contactNumber', 'Contact Number'],
  ['lineOfBusiness', 'Member LOB'],
  ['createdAt', 'Member Added Date'],
  ['hasMobileAppAccount', 'Has Mobile App Account'],
  ['enrolledInSms', 'Enrolled in SMS'],
] as const satisfies readonly MemberReportColumnDefinition[];

const UTILIZING_MEMBERS_BY_AGE_GROUP_COLUMN_DEFINITIONS = [
  ['fullName', 'Member Name'],
  ['memberIdNumber', 'MemberId'],
  ['status', 'Member Status'],
  ['contactNumber', 'Contact Number'],
  ['dateOfServiceMonthYear', 'Date of Service Month + Year'],
  ['ageGroup', 'Age Group'],
  ['age', 'Age'],
  ['totalRides', 'Count Ride Segments by Member'],
] as const satisfies readonly MemberReportColumnDefinition[];

export const MEMBER_DASHBOARD_CHART_TITLES = {
  TOP_NO_SHOW: 'Top 5 Incomplete - member no show',
  TOP_HIGH_MILEAGE: 'Top 5 Members High Mileage',
  ACTIVE_INACTIVE: 'Member Active/Inactive',
  TOP_USED_TAGS: 'Top 5 Most Used Tags',
  TOP_MOST_TAGS_USED: 'Top 5 Most Tags Used',
  UNIQUE_BY_RENDERING_SERVICE: 'Unique Members by Rendering Service',
  UNIQUE_BY_LOB: 'Unique Members by LOB',
  UNIQUE_BY_COUNTY: 'Unique Utilizing Members by County',
} as const;

export const MEMBER_MONTHLY_CHART_TITLES = {
  DISTINCT_UTILIZING_MEMBERS: 'Distinct Number of Utilizing Members',
  AVG_RIDE_AND_COST_PER_MEMBER: 'Avg Ride and Cost Per Member',
  AVG_RIDE_AND_COST_PER_MEMBER_DATA: 'Average Ride and Cost Per Member Data',
  ACTIVE_INACTIVE: 'Member Active/Inactive',
  CONSECUTIVE_NO_SHOWS: 'Members with Consecutive No Shows',
  CONSECUTIVE_NO_SHOWS_3_PLUS: 'Members with Consecutive No Shows (3 or more)',
  NEW_MEMBERS_ADDED: 'New Members Added to System',
  NEW_MEMBERS_ADDED_TO_THE_SYSTEM: 'New Members Added to the System',
  UTILIZING_MEMBERS_BY_AGE_GROUP: 'Utilizing Members by Age Group',
} as const;

export const MEMBER_DASHBOARD_CHART_DESCRIPTIONS = {
  TOP_NO_SHOW:
    'Highlights the top 5 members with the highest number of missed rides (no shows) during the selected date range, helping identify patterns that may require follow-up or intervention.',
  TOP_HIGH_MILEAGE:
    'Shows the top 5 members with the highest total miles traveled from completed rides within the selected date range, helping identify members with the greatest transportation utilization.',
  ACTIVE_INACTIVE:
    'Shows the number of active and inactive members in the specific date range by Eligibility. helping track changes in member engagement over time.',
  TOP_USED_TAGS:
    'Shows the top 5 most frequently used member tags based on completed rides within the selected date range, helping identify common classifications or attributes across members.',
  UNIQUE_BY_RENDERING_SERVICE:
    'Displays the number of unique members using each transportation rendering service from completed rides within the selected date range, helping you understand service utilization across your population.',
  UNIQUE_BY_LOB:
    'Shows the number of unique members by line of business based on completed rides within the selected date range, helping you analyze member distribution across different programs.',
  UNIQUE_BY_COUNTY:
    'Displays the number of unique members using transportation services by pickup county from completed rides within the selected date range, helping identify geographic usage patterns.',
} as const;

export const MEMBER_MONTHLY_CHART_DESCRIPTIONS = {
  DISTINCT_UTILIZING_MEMBERS:
    'Shows the total number of unique members who used transportation services based on completed rides each month, helping track participation over time.',
  AVG_RIDE_AND_COST_PER_MEMBER:
    'Displays the average number of rides and average cost per member based on completed rides each month, helping monitor usage and cost efficiency.',
  ACTIVE_INACTIVE:
    'Shows the number of active and inactive members each month by Eligibility, helping track changes in member engagement over time.',
  CONSECUTIVE_NO_SHOWS_3_PLUS:
    'Highlights members who missed (no show) three or more rides in a row during each month, helping identify recurring no-show behavior that may need intervention.',
  NEW_MEMBERS_ADDED:
    'Displays the number of new members added each month, helping track growth in the member population.',
  UTILIZING_MEMBERS_BY_AGE_GROUP:
    'Shows the number of members using transportation services based on completed rides by age group each month, helping identify demographic usage trends.',
} as const;

const buildReportTitleMap = (
  reportIds: readonly MemberReportId[],
  title: string
) =>
  reportIds.reduce<Partial<Record<MemberReportId, string>>>(
    (acc, reportId) => ({
      ...acc,
      [reportId]: title,
    }),
    {}
  );

const MEMBER_REPORT_PAGE_TITLES: Partial<Record<MemberReportId, string>> = {
  ...buildReportTitleMap(
    [MEMBER_REPORT_IDS.NO_SHOW_MEMBERS],
    'Top 5 Members No Show Count'
  ),
  ...buildReportTitleMap(
    [MEMBER_REPORT_IDS.HIGH_MILEAGE_MEMBERS],
    MEMBER_DASHBOARD_CHART_TITLES.TOP_HIGH_MILEAGE
  ),
  ...buildReportTitleMap(
    [MEMBER_REPORT_IDS.ACTIVE_MEMBERS],
    MEMBER_DASHBOARD_CHART_TITLES.ACTIVE_INACTIVE
  ),
  ...buildReportTitleMap(
    TAG_REPORT_IDS,
    MEMBER_DASHBOARD_CHART_TITLES.TOP_USED_TAGS
  ),
  ...buildReportTitleMap(
    RENDERING_SERVICE_REPORT_IDS,
    MEMBER_DASHBOARD_CHART_TITLES.UNIQUE_BY_RENDERING_SERVICE
  ),
  ...buildReportTitleMap(
    [MEMBER_REPORT_IDS.MEMBERS_BY_LOB],
    MEMBER_DASHBOARD_CHART_TITLES.UNIQUE_BY_LOB
  ),
  ...buildReportTitleMap(
    [MEMBER_REPORT_IDS.MEMBERS_BY_COUNTY],
    MEMBER_DASHBOARD_CHART_TITLES.UNIQUE_BY_COUNTY
  ),
};

const MEMBER_REPORT_PAGE_DESCRIPTIONS: Partial<Record<MemberReportId, string>> =
  {
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.NO_SHOW_MEMBERS],
      MEMBER_DASHBOARD_CHART_DESCRIPTIONS.TOP_NO_SHOW
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.HIGH_MILEAGE_MEMBERS],
      MEMBER_DASHBOARD_CHART_DESCRIPTIONS.TOP_HIGH_MILEAGE
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.ACTIVE_MEMBERS],
      MEMBER_DASHBOARD_CHART_DESCRIPTIONS.ACTIVE_INACTIVE
    ),
    ...buildReportTitleMap(
      TAG_REPORT_IDS,
      MEMBER_DASHBOARD_CHART_DESCRIPTIONS.TOP_USED_TAGS
    ),
    ...buildReportTitleMap(
      RENDERING_SERVICE_REPORT_IDS,
      MEMBER_DASHBOARD_CHART_DESCRIPTIONS.UNIQUE_BY_RENDERING_SERVICE
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.MEMBERS_BY_LOB],
      MEMBER_DASHBOARD_CHART_DESCRIPTIONS.UNIQUE_BY_LOB
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.MEMBERS_BY_COUNTY],
      MEMBER_DASHBOARD_CHART_DESCRIPTIONS.UNIQUE_BY_COUNTY
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.MONTHLY_MEMBER_ACTIVITY],
      MEMBER_MONTHLY_CHART_DESCRIPTIONS.DISTINCT_UTILIZING_MEMBERS
    ),
    ...buildReportTitleMap(
      AVG_RIDE_AND_COST_REPORT_IDS,
      MEMBER_MONTHLY_CHART_DESCRIPTIONS.AVG_RIDE_AND_COST_PER_MEMBER
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.MONTHLY_MEMBER_ACTIVE_INACTIVE],
      MEMBER_MONTHLY_CHART_DESCRIPTIONS.ACTIVE_INACTIVE
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.CONSECUTIVE_NO_SHOW],
      MEMBER_MONTHLY_CHART_DESCRIPTIONS.CONSECUTIVE_NO_SHOWS_3_PLUS
    ),
    ...buildReportTitleMap(
      [MEMBER_REPORT_IDS.MONTHLY_MEMBERS_CREATED],
      MEMBER_MONTHLY_CHART_DESCRIPTIONS.NEW_MEMBERS_ADDED
    ),
    ...buildReportTitleMap(
      [
        MEMBER_REPORT_IDS.UTILIZING_MEMBERS_BY_AGE_GROUP,
        MEMBER_REPORT_IDS.AGE_GROUP_ANALYSIS,
      ],
      MEMBER_MONTHLY_CHART_DESCRIPTIONS.UTILIZING_MEMBERS_BY_AGE_GROUP
    ),
  };

const normalizeChartTitle = (value: string) =>
  value.trim().toLowerCase().replace(/[/-]+/g, ' ').replace(/\s+/g, ' ');

const toDataTitle = (value: string) =>
  /\bdata$/i.test(value) ? value : `${value}`;

const CHART_REPORT_CANDIDATES: Record<string, MemberReportId[]> = {
  [MEMBER_DASHBOARD_CHART_TITLES.TOP_NO_SHOW]: [
    MEMBER_REPORT_IDS.NO_SHOW_MEMBERS,
    MEMBER_REPORT_IDS.CONSECUTIVE_NO_SHOW,
  ],
  [MEMBER_DASHBOARD_CHART_TITLES.TOP_HIGH_MILEAGE]: [
    MEMBER_REPORT_IDS.HIGH_MILEAGE_MEMBERS,
  ],
  [MEMBER_DASHBOARD_CHART_TITLES.ACTIVE_INACTIVE]: [
    MEMBER_REPORT_IDS.ACTIVE_MEMBERS,
  ],
  [MEMBER_DASHBOARD_CHART_TITLES.TOP_USED_TAGS]: [...TAG_REPORT_IDS],
  [MEMBER_DASHBOARD_CHART_TITLES.TOP_MOST_TAGS_USED]: [...TAG_REPORT_IDS],
  [MEMBER_DASHBOARD_CHART_TITLES.UNIQUE_BY_RENDERING_SERVICE]: [
    ...RENDERING_SERVICE_REPORT_IDS,
  ],
  [MEMBER_DASHBOARD_CHART_TITLES.UNIQUE_BY_LOB]: [
    MEMBER_REPORT_IDS.MEMBERS_BY_LOB,
  ],
  [MEMBER_DASHBOARD_CHART_TITLES.UNIQUE_BY_COUNTY]: [
    MEMBER_REPORT_IDS.MEMBERS_BY_COUNTY,
  ],
};

const MONTHLY_REPORT_CANDIDATES: Record<string, MemberReportId[]> = {
  [MEMBER_MONTHLY_CHART_TITLES.DISTINCT_UTILIZING_MEMBERS]: [
    MEMBER_REPORT_IDS.MONTHLY_MEMBER_ACTIVITY,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.AVG_RIDE_AND_COST_PER_MEMBER]: [
    ...AVG_RIDE_AND_COST_REPORT_IDS,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.AVG_RIDE_AND_COST_PER_MEMBER_DATA]: [
    ...AVG_RIDE_AND_COST_REPORT_IDS,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.ACTIVE_INACTIVE]: [
    MEMBER_REPORT_IDS.MONTHLY_MEMBER_ACTIVE_INACTIVE,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.CONSECUTIVE_NO_SHOWS]: [
    MEMBER_REPORT_IDS.CONSECUTIVE_NO_SHOW,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.CONSECUTIVE_NO_SHOWS_3_PLUS]: [
    MEMBER_REPORT_IDS.CONSECUTIVE_NO_SHOW,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.NEW_MEMBERS_ADDED]: [
    MEMBER_REPORT_IDS.MONTHLY_MEMBERS_CREATED,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.NEW_MEMBERS_ADDED_TO_THE_SYSTEM]: [
    MEMBER_REPORT_IDS.MONTHLY_MEMBERS_CREATED,
  ],
  [MEMBER_MONTHLY_CHART_TITLES.UTILIZING_MEMBERS_BY_AGE_GROUP]: [
    MEMBER_REPORT_IDS.UTILIZING_MEMBERS_BY_AGE_GROUP,
    MEMBER_REPORT_IDS.AGE_GROUP_ANALYSIS,
  ],
};

const buildColumn = (
  field: string,
  label: string,
  order: number
): MemberReportColumnOverride => ({
  field,
  label,
  order,
  defaultVisible: true,
});

const buildColumns = (
  definitions: readonly MemberReportColumnDefinition[]
): MemberReportColumnOverride[] =>
  definitions.map(([field, label], index) => buildColumn(field, label, index));

const buildCommonMemberColumns = (lastColumn: {
  field: string;
  label: string;
}): MemberReportColumnOverride[] => [
  ...buildColumns(COMMON_MEMBER_COLUMN_DEFINITIONS),
  buildColumn(
    lastColumn.field,
    lastColumn.label,
    COMMON_MEMBER_COLUMN_DEFINITIONS.length
  ),
];

const buildAggregateMemberColumns = ({
  groupLabel,
  countField = 'memberCount',
  countLabel,
}: {
  groupLabel: string;
  countField?: string;
  countLabel: string;
}): MemberReportColumnOverride[] => [
  ...buildColumns(AGGREGATE_MEMBER_COLUMN_DEFINITIONS),
  buildColumn(
    'groupKey',
    groupLabel,
    AGGREGATE_MEMBER_COLUMN_DEFINITIONS.length
  ),
  buildColumn(
    countField,
    countLabel,
    AGGREGATE_MEMBER_COLUMN_DEFINITIONS.length + 1
  ),
];

const buildMemberMonthlyColumns = (
  trailingDefinitions: readonly MemberReportColumnDefinition[]
): MemberReportColumnOverride[] => [
  ...buildColumns(MONTHLY_MEMBER_COLUMN_DEFINITIONS),
  ...trailingDefinitions.map(([field, label], index) =>
    buildColumn(field, label, MONTHLY_MEMBER_COLUMN_DEFINITIONS.length + index)
  ),
];

const withoutMemberLob = (columns: MemberReportColumnOverride[]) =>
  columns.filter((column) => column.field !== 'lineOfBusiness');

const MEMBER_REPORT_COLUMN_OVERRIDES: Record<
  string,
  MemberReportColumnOverride[]
> = {
  [MEMBER_REPORT_IDS.NO_SHOW_MEMBERS]: withoutMemberLob(
    buildCommonMemberColumns({
      field: 'noShowCount',
      label: 'Count Member No Show Rides',
    })
  ),
  [MEMBER_REPORT_IDS.HIGH_MILEAGE_MEMBERS]: withoutMemberLob(
    buildCommonMemberColumns({
      field: 'totalMiles',
      label: 'Sum Mileage',
    })
  ),
  [MEMBER_REPORT_IDS.ACTIVE_MEMBERS]: buildCommonMemberColumns({
    field: 'physicalAddress',
    label: 'Physical Address',
  }),
  ...Object.fromEntries(
    TAG_REPORT_IDS.map((reportId) => [
      reportId,
      buildAggregateMemberColumns({
        groupLabel: 'Ride Tag',
        countLabel: 'Count of Distinct Members',
      }),
    ])
  ),
  [MEMBER_REPORT_IDS.MEMBERS_BY_COUNTY]: buildAggregateMemberColumns({
    groupLabel: 'Pickup Address County',
    countLabel: 'Count Distinct Members',
  }),
};

const MEMBER_CHART_COLUMN_OVERRIDES: Record<
  string,
  MemberReportColumnOverride[]
> = {
  [normalizeChartTitle(MEMBER_MONTHLY_CHART_TITLES.DISTINCT_UTILIZING_MEMBERS)]:
    withoutMemberLob(
      buildMemberMonthlyColumns([
        ['totalRides', 'Count Ride segments by Member'],
      ])
    ),
  [normalizeChartTitle(
    MEMBER_MONTHLY_CHART_TITLES.AVG_RIDE_AND_COST_PER_MEMBER
  )]: buildColumns(AVG_RIDE_AND_COST_COLUMN_DEFINITIONS),
  [normalizeChartTitle(
    MEMBER_MONTHLY_CHART_TITLES.AVG_RIDE_AND_COST_PER_MEMBER_DATA
  )]: buildColumns(AVG_RIDE_AND_COST_COLUMN_DEFINITIONS),
  [normalizeChartTitle(MEMBER_MONTHLY_CHART_TITLES.ACTIVE_INACTIVE)]:
    buildColumns(MEMBER_ACTIVE_INACTIVE_MONTHLY_COLUMN_DEFINITIONS),
  [normalizeChartTitle(MEMBER_MONTHLY_CHART_TITLES.NEW_MEMBERS_ADDED)]:
    buildColumns(NEW_MEMBERS_ADDED_COLUMN_DEFINITIONS),
  [normalizeChartTitle(
    MEMBER_MONTHLY_CHART_TITLES.NEW_MEMBERS_ADDED_TO_THE_SYSTEM
  )]: buildColumns(NEW_MEMBERS_ADDED_COLUMN_DEFINITIONS),
  [normalizeChartTitle(MEMBER_MONTHLY_CHART_TITLES.CONSECUTIVE_NO_SHOWS)]:
    withoutMemberLob(
      buildMemberMonthlyColumns([
        [
          'consecutiveNoShowCount',
          'Count No Show (Incomplete - member no show)',
        ],
      ])
    ),
  [normalizeChartTitle(
    MEMBER_MONTHLY_CHART_TITLES.CONSECUTIVE_NO_SHOWS_3_PLUS
  )]: withoutMemberLob(
    buildMemberMonthlyColumns([
      ['consecutiveNoShowCount', 'Count No Show (Incomplete - member no show)'],
    ])
  ),
  [normalizeChartTitle(
    MEMBER_MONTHLY_CHART_TITLES.UTILIZING_MEMBERS_BY_AGE_GROUP
  )]: buildColumns(UTILIZING_MEMBERS_BY_AGE_GROUP_COLUMN_DEFINITIONS),
};

const normalizeConfigs = (configs: MemberReportConfig[] | undefined) =>
  Array.isArray(configs) ? configs : [];

const getNavigateUrlByCandidates = (
  configs: MemberReportConfig[] | undefined,
  chartTitle: string,
  candidatesMap: Record<string, MemberReportId[]>
) => {
  const normalizedTitle = normalizeChartTitle(chartTitle);
  const matchedEntry = Object.entries(candidatesMap).find(
    ([title]) => normalizeChartTitle(title) === normalizedTitle
  );
  const candidateIds = matchedEntry?.[1] ?? [];
  const normalizedConfigs = normalizeConfigs(configs);

  const matchedCandidateId = candidateIds.find((candidateId) =>
    normalizedConfigs.some((config) => config.reportId === candidateId)
  );

  return matchedCandidateId;
};

export const getMemberDashboardNavigateUrl = (
  configs: MemberReportConfig[] | undefined,
  chartTitle: string
) => getNavigateUrlByCandidates(configs, chartTitle, CHART_REPORT_CANDIDATES);

export const getMemberMonthlyNavigateUrl = (
  configs: MemberReportConfig[] | undefined,
  chartTitle: string
) => getNavigateUrlByCandidates(configs, chartTitle, MONTHLY_REPORT_CANDIDATES);

export const getMemberReportPageTitle = (
  reportId?: string,
  chartTitle?: string,
  fallbackTitle?: string
) =>
  chartTitle ??
  (reportId
    ? MEMBER_REPORT_PAGE_TITLES[reportId as MemberReportId]
    : undefined) ??
  fallbackTitle ??
  '';

export const getMemberReportPageDescription = (
  reportId?: string,
  chartTooltip?: string
) =>
  chartTooltip ??
  (reportId
    ? MEMBER_REPORT_PAGE_DESCRIPTIONS[reportId as MemberReportId]
    : undefined);

export const getMemberReportBreadcrumbTitle = (
  reportId?: string,
  chartTitle?: string,
  fallbackTitle?: string
) => {
  const pageTitle = getMemberReportPageTitle(
    reportId,
    chartTitle,
    fallbackTitle
  );

  return pageTitle ? toDataTitle(pageTitle) : '';
};

const numberFields = new Set([
  'age',
  'memberId',
  'memberCount',
  'totalRides',
  'totalMiles',
  'totalCostPerMember',
  'avgCostPerMember',
  'averageMiles',
  'noShowCount',
  'consecutiveNoShowCount',
  'rideId',
  'rideSegmentId',
  'cost',
  'actualMiles',
  'estMiles',
]);

const dateFields = new Set([
  'dob',
  'createdAt',
  'dateOfService',
  'firstDateOfService',
  'lastDateOfService',
]);

const timeFields = new Set(['pickupTime', 'actualPickupTime']);
const statusFields = new Set(['status', 'rideStatus', 'statusGroup']);
const booleanFields = new Set([
  'willCallIndicator',
  'hasMobileAppAccount',
  'enrolledInSms',
  'smsIndicator',
]);

export const resolveMemberColumnType = (field: string) => {
  if (statusFields.has(field)) return 'status';
  if (dateFields.has(field)) return 'date';
  if (timeFields.has(field)) return 'time';
  if (numberFields.has(field)) return 'number';
  if (booleanFields.has(field)) return 'boolean';

  return 'text';
};

export const buildMemberReportColumns = (
  config?: MemberReportConfig | null,
  chartTitle?: string
): any[] => {
  const chartOverride =
    chartTitle != null
      ? MEMBER_CHART_COLUMN_OVERRIDES[normalizeChartTitle(chartTitle)]
      : undefined;
  const reportOverride = MEMBER_REPORT_COLUMN_OVERRIDES[config?.reportId ?? ''];
  const configuredColumns =
    reportOverride ?? chartOverride ?? config?.columns ?? [];

  const columns = [...configuredColumns].sort(
    (left, right) => (left?.order ?? 0) - (right?.order ?? 0)
  );

  return columns.map((column) => ({
    key: column.field,
    label: column.label,
    type: resolveMemberColumnType(column.field),
    show: column.defaultVisible,
  }));
};

export const buildMemberReportAllColumns = (
  config?: MemberReportConfig | null,
  chartTitle?: string
) =>
  buildMemberReportColumns(config, chartTitle).map((column) => ({
    ...column,
    show: true,
  }));

export const MEMBER_DRILL_THROUGH_VISIBLE_COLUMNS = REPORT_VISIBLE_COLUMNS[
  RIDE_REPORT_TYPES.RIDES_REPORT
].flatMap((key) => (key === 'memberIdNumber' ? [key, 'lob'] : [key]));

export const buildMemberDrillThroughColumns = () =>
  MEMBER_DRILL_THROUGH_VISIBLE_COLUMNS.map((key) => {
    const column = rideBaseColumns.find((item) => item.key === key);

    if (!column) return null;

    return {
      ...column,
      sortingField: column.key === 'dateOfService' ? 'rideDate' : column.key,
      show: true,
    };
  }).filter(Boolean);

const MEMBER_DRILL_THROUGH_HIDDEN_COLUMN_KEYS = [
  'firstDateOfService',
  'lastDateOfService',
  'countOnTimeTrips',
  'countLateTrips',
  'percentageOnTime',
  'percentageLate',
  'countRide',
];

export const buildMemberDrillThroughAllColumns = () => {
  const memberLobColumn = rideBaseColumns.find(
    (column) => column.key === 'lob'
  );

  return rideBaseColumns
    .filter(
      (column) =>
        column.key !== 'lob' &&
        !MEMBER_DRILL_THROUGH_HIDDEN_COLUMN_KEYS.includes(column.key)
    )
    .flatMap((column) =>
      column.key === 'memberIdNumber' && memberLobColumn
        ? [column, memberLobColumn]
        : [column]
    )
    .map((column) => ({
      ...column,
      sortingField: column.key === 'dateOfService' ? 'rideDate' : column.key,
      show: true,
    }));
};

const getNoShowCountValue = (value: any): number => {
  if (Array.isArray(value)) {
    return value.reduce(
      (sum, item) =>
        sum +
        Number(
          item?.noShowCount ??
            item?.memberCount ??
            item?.passengerCount ??
            item?.count ??
            item?.totalCount ??
            0
        ),
      0
    );
  }

  if (value && typeof value === 'object') {
    return Number(
      value?.noShowCount ??
        value?.memberCount ??
        value?.passengerCount ??
        value?.count ??
        value?.totalCount ??
        0
    );
  }

  return Number(value ?? 0);
};

export const normalizeNoShowData = (payload: any): NoShowMonthItem[] => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload.map((item: any) => ({
      month: String(item?.month ?? ''),
      noShowCount: getNoShowCountValue(item),
    }));
  }

  if (
    payload?.month &&
    typeof payload.month === 'object' &&
    !Array.isArray(payload.month)
  ) {
    return Object.entries(payload.month).map(([month, value]) => ({
      month,
      noShowCount: getNoShowCountValue(value),
    }));
  }

  return [];
};

const toFiniteNumber = (value: unknown): number => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getAvgRidesPerMemberValue = (
  item: AvgRideCostPerMemberApiItem | AvgRideCostPerMemberApiResponse
) =>
  toFiniteNumber(
    item.avgRidesPerMember ?? item.avrRidesPerMember ?? item.avRidesPerMember
  );

export const normalizeAvgRideCostPerMemberData = (
  payload?: AvgRideCostPerMemberApiResponse
) => {
  const monthlyRideStats = Array.isArray(payload?.monthlyRideStats)
    ? payload.monthlyRideStats
    : [];
  const avgCostPerMember = toFiniteNumber(payload?.avgCostPerMember);
  const chartData: AvgRideCostPerMemberItem[] = monthlyRideStats
    .map((item) => ({
      month: String(item?.month ?? ''),
      avgRidesPerMember: getAvgRidesPerMemberValue(item),
      avgCostPerMember: toFiniteNumber(
        item?.avgCostPerMember ?? avgCostPerMember
      ),
    }))
    .filter((item) => item.month)
    .sort((a, b) => a.month.localeCompare(b.month));

  const fallbackAvgRides =
    chartData.length > 0
      ? chartData.reduce((sum, item) => sum + item.avgRidesPerMember, 0) /
        chartData.length
      : 0;

  return {
    data: chartData,
    summary: {
      avgRidesPerMember:
        getAvgRidesPerMemberValue(payload ?? {}) || fallbackAvgRides,
      avgCostPerMember,
      avgCostPerRide: toFiniteNumber(payload?.avgCostPerRide),
    },
  };
};

export const normalizeMemberActiveInactiveData = (
  payload?: MemberActiveInactiveApiItem[]
): MemberActiveInactiveItem[] =>
  (Array.isArray(payload) ? payload : [])
    .map((item) => {
      const active = toFiniteNumber(item?.activeMembers);
      const inactive = toFiniteNumber(item?.inactiveMembers);
      return {
        month: String(item?.month ?? ''),
        active,
        total: active + inactive,
      };
    })
    .filter((item) => item.month)
    .sort((a, b) => a.month.localeCompare(b.month));
